import { useState } from "react";
import { WORD_PACKS, type WordPack } from "../data/word-packs";
import { TEACHING_LEVELS } from "../data/teaching-levels";
import { GameImage } from "../game/ui/GameImage";

interface Props {
  /** Called when the host has selected both a pack and a level. The lessonId
   *  encodes both as `pack:<packId>::L<n>` so the multiplayer protocol stays
   *  opaque-string-based and the rest of the stack reads it like any lesson. */
  onPick: (lessonId: string) => void;
  onCancel: () => void;
}

/** Battle host's two-step picker. Mirrors PackLevelPicker but emits a
 *  synthetic lesson id instead of navigating directly. */
export function BattlePackPicker({ onPick, onCancel }: Props) {
  const [chosenPack, setChosenPack] = useState<WordPack | null>(null);

  if (!chosenPack) {
    return (
      <div className="picker-root">
        <div className="picker-topbar">
          <button className="home-settings-btn" onClick={onCancel} aria-label="Back">←</button>
          <h1>Pick a word pack</h1>
        </div>
        <p className="picker-sub">All players will learn these 5 words.</p>
        <div className="picker-grid pack-grid">
          {WORD_PACKS.map((pack) => (
            <button
              key={pack.id}
              className="picker-tile pack-tile"
              onClick={() => setChosenPack(pack)}
            >
              <div className="pack-tile-images">
                {pack.words.slice(0, 5).map((w) => (
                  <span key={w.id} className="pack-tile-image">
                    <GameImage src={w.imageUrl} alt={w.english} fallbackLabel="★" />
                  </span>
                ))}
              </div>
              <div className="pack-tile-text">
                <strong>{pack.title}</strong>
                <span className="t-muted">{pack.subtitle}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="picker-root">
      <div className="picker-topbar">
        <button className="home-settings-btn" onClick={() => setChosenPack(null)} aria-label="Back to packs">←</button>
        <h1>{chosenPack.title}</h1>
      </div>
      <p className="picker-sub">Choose a level for the race.</p>
      <div className="picker-words">
        {chosenPack.words.map((w) => (
          <span key={w.id} className="picker-word-pill">
            <GameImage src={w.imageUrl} alt={w.english} fallbackLabel="★" />
            <strong>{w.english}</strong>
          </span>
        ))}
      </div>
      <div className="picker-grid level-grid">
        {TEACHING_LEVELS.map((lvl) => (
          <button
            key={lvl.index}
            className="picker-tile level-tile"
            onClick={() => onPick(`pack:${chosenPack.id}::L${lvl.index}`)}
          >
            <div className="level-tile-num">{lvl.index}</div>
            <div className="level-tile-text">
              <strong>{lvl.title}</strong>
              <span className="t-muted">{lvl.subtitle}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
